/**
 * Looping logo strip (shared: ab-hospitality, logo-strip).
 * PHP renders one group of logos (images are `skip-lazy` — a lazy placeholder would give wrong widths).
 * JS repeats the logos inside it until the group is at least as wide as the strip, then clones the whole
 * group → two identical halves; CSS moves the track 0 → -50% (seamless) once the strip gets `is-ready`.
 * The duration is set from the group width so the speed is constant.
 * Measured only when every logo has its real size; rebuilt if a logo loads later or the width changes.
 * Runs on all breakpoints (not a scroll effect).
 *
 * @param {HTMLElement} strip  overflow: hidden wrapper
 * @param {Object} o
 * @param {string} o.track  track selector (inside strip)
 * @param {string} o.group  group selector (inside track)
 * @param {number|function(): number} o.speed  px per second (lower = slower)
 */
const isLoaded = (img) => img.complete && img.naturalWidth > 0;

export const initLogoMarquee = (strip, {track: trackSel, group: groupSel, speed}) => {
    const track = strip.querySelector(trackSel);
    const group = track?.querySelector(groupSel);
    if (!group || !group.children.length) return;

    const originals = [...group.children];
    const images = originals.map((logo) => logo.querySelector('img')).filter(Boolean);
    const getSpeed = typeof speed === 'function' ? speed : () => speed;

    const build = () => {
        if (!images.every(isLoaded) || !strip.offsetWidth) return;

        // reset to the original set
        track.querySelectorAll(`${groupSel}:not(:first-child)`).forEach((el) => el.remove());
        group.replaceChildren(...originals);

        // fill one group to at least the strip width (with a sane cap)
        let guard = 0;
        while (group.offsetWidth < strip.offsetWidth && guard < 30) {
            originals.forEach((logo) => group.appendChild(logo.cloneNode(true)));
            guard++;
        }

        const copy = group.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        track.appendChild(copy);

        track.style.animationDuration = `${group.offsetWidth / getSpeed()}s`;
        strip.classList.add('is-ready');
    };

    images.forEach((img) => {
        if (!isLoaded(img)) img.addEventListener('load', build, {once: true});
    });
    build();

    let lastWidth = window.innerWidth;
    window.addEventListener('resize', () => {
        if (window.innerWidth === lastWidth) return;
        lastWidth = window.innerWidth;
        build();
    });
};
